import { Fragment, useEffect, useState, useCallback } from 'react';
import { api, resolveImageUrl } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface RegionalRow {
  marketplace: string; country: string; currency: string; local_currency: string;
  order_count: number; units: number; revenue: number; cogs: number; profit: number;
  margin_pct: number; fba_fees: number; commission: number; promo: number;
  ppc_spend: number; ppc_sales: number; return_units: number; return_count: number;
  net_proceeds: number; acos: number; tacos: number; refund_rate: number; exchange_rate: number;
}

interface RegionalTotals {
  order_count: number; units: number; revenue: number; cogs: number; profit: number;
  margin_pct: number; fba_fees: number; commission: number; promo: number;
  ppc_spend: number; ppc_sales: number; return_units: number; return_count: number;
  net_proceeds: number; acos: number; tacos: number; refund_rate: number;
}

interface RegionalResponse { rows: RegionalRow[]; totals: RegionalTotals; start: string; end: string; }

interface ProductRow {
  asin: string; sku: string; title: string; imageUrl: string | null;
  orders: number; units: number; revenue: number; cogs: number; profit: number; margin: number; avgPrice: number;
}
interface ProductTotals { orders: number; units: number; revenue: number; cogs: number; profit: number; margin: number; }
interface ProductResponse { products: ProductRow[]; totals: ProductTotals; start: string; end: string; }

interface ExpandRow {
  marketplace: string; currency: string; orders: number; units: number;
  revenueLocal: number; revenueUsd: number;
  cogs?: number; fbaFees?: number; commission?: number; profit?: number;
  margin?: number; ppcSpend?: number; ppcSales?: number; acos?: number; tacos?: number;
  refunds?: number; promos?: number;
}
interface ExpandData {
  byProduct: Record<string, ExpandRow[]>;
  byMarket: Record<string, Array<ExpandRow & { sku: string; asin: string; parentAsin: string; parentNickname: string | null }>>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtUsd(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}
function fmtNum(n: number) { return new Intl.NumberFormat('en-US').format(n); }
function pct(n: number) { return `${n.toFixed(1)}%`; }

const FLAG: Record<string, string> = {
  'United States': '\u{1F1FA}\u{1F1F8}', 'Canada': '\u{1F1E8}\u{1F1E6}',
  'Mexico': '\u{1F1F2}\u{1F1FD}', 'United Kingdom': '\u{1F1EC}\u{1F1E7}',
  'Germany': '\u{1F1E9}\u{1F1EA}', 'France': '\u{1F1EB}\u{1F1F7}',
  'Italy': '\u{1F1EE}\u{1F1F9}', 'Spain': '\u{1F1EA}\u{1F1F8}',
  'Netherlands': '\u{1F1F3}\u{1F1F1}', 'Sweden': '\u{1F1F8}\u{1F1EA}',
};

type Tab = 'region' | 'product';

const MP_NAMES: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
};

// ── Column definitions ──────────────────────────────────────────────────────

interface ColDef {
  key: string;
  label: string;
  getValue: (r: RegionalRow, totals?: RegionalTotals) => string;
  getClass?: (r: RegionalRow) => string;
  align?: 'left' | 'right';
  defaultVisible?: boolean;
}

const payout = (r: { revenue: number; fba_fees: number; commission: number }) =>
  r.revenue - r.fba_fees - r.commission;

const ppcShare = (r: { ppc_sales: number; revenue: number }) =>
  r.revenue > 0 ? (r.ppc_sales / r.revenue) * 100 : 0;

const ALL_COLUMNS: ColDef[] = [
  { key: 'country', label: 'Country', getValue: r => `${FLAG[r.country] ?? ''} ${r.country}`, align: 'left', defaultVisible: true },
  { key: 'units', label: 'Units', getValue: r => fmtNum(r.units), align: 'right', defaultVisible: true },
  { key: 'revenue', label: 'Revenue', getValue: r => fmtUsd(r.revenue), align: 'right', defaultVisible: true },
  { key: 'payout', label: 'Payout', getValue: r => fmtUsd(payout(r)), align: 'right', defaultVisible: true },
  { key: 'cogs', label: 'COGS', getValue: r => fmtUsd(r.cogs), align: 'right', defaultVisible: true },
  { key: 'profit', label: 'Profit', getValue: r => fmtUsd(r.profit), align: 'right', defaultVisible: true,
    getClass: r => r.profit >= 0 ? 'text-green-600' : 'text-red-600' },
  { key: 'margin', label: 'Margin', getValue: r => pct(r.margin_pct), align: 'right', defaultVisible: true,
    getClass: r => r.margin_pct >= 0 ? 'text-green-600' : 'text-red-600' },
  { key: 'fba_fees', label: 'FBA Fees', getValue: r => fmtUsd(r.fba_fees), align: 'right', defaultVisible: true },
  { key: 'commission', label: 'Commission', getValue: r => fmtUsd(r.commission), align: 'right', defaultVisible: true },
  { key: 'ppc_spend', label: 'PPC Spend', getValue: r => fmtUsd(r.ppc_spend), align: 'right', defaultVisible: true },
  { key: 'ppc_sales', label: 'PPC Sales', getValue: r => fmtUsd(r.ppc_sales), align: 'right', defaultVisible: true },
  { key: 'ppc_share', label: 'PPC Share', getValue: r => ppcShare(r) > 0 ? pct(ppcShare(r)) : '-', align: 'right', defaultVisible: true },
  { key: 'acos', label: 'ACoS', getValue: r => r.acos > 0 ? pct(r.acos) : '-', align: 'right', defaultVisible: true },
  { key: 'tacos', label: 'TACoS', getValue: r => r.tacos > 0 ? pct(r.tacos) : '-', align: 'right', defaultVisible: true },
  { key: 'refunds', label: 'Refunds', getValue: r => fmtNum(r.return_units), align: 'right', defaultVisible: true },
  { key: 'refund_pct', label: 'Refund %', getValue: r => r.refund_rate > 0 ? (r.refund_rate > 100 ? '>100%' : pct(r.refund_rate)) : '-', align: 'right', defaultVisible: true },
  { key: 'promos', label: 'Promos', getValue: r => fmtUsd(r.promo), align: 'right', defaultVisible: true },
];

const STORAGE_KEY = 'sellvana_sales_columns';

function loadColumnConfig(): { visible: string[]; order: string[] } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {
    visible: ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key),
    order: ALL_COLUMNS.map(c => c.key),
  };
}

function saveColumnConfig(cfg: { visible: string[]; order: string[] }) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
}

// ── Column configurator popover ─────────────────────────────────────────────

function ColumnConfig({ config, onChange }: {
  config: { visible: string[]; order: string[] };
  onChange: (cfg: { visible: string[]; order: string[] }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const ordered = config.order.map(k => ALL_COLUMNS.find(c => c.key === k)!).filter(Boolean);

  const toggle = (key: string) => {
    const vis = config.visible.includes(key)
      ? config.visible.filter(k => k !== key)
      : [...config.visible, key];
    const next = { ...config, visible: vis };
    onChange(next); saveColumnConfig(next);
  };

  const move = (from: number, to: number) => {
    const order = [...config.order];
    const [item] = order.splice(from, 1);
    order.splice(to, 0, item);
    const next = { ...config, order };
    onChange(next); saveColumnConfig(next);
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-1">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
        Columns
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-xl border border-gray-200 bg-white shadow-lg p-2 max-h-80 overflow-y-auto">
            <p className="text-[10px] uppercase text-gray-400 font-semibold px-2 py-1">Drag to reorder</p>
            {ordered.map((col, i) => (
              <div key={col.key}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={e => e.preventDefault()}
                onDrop={() => { if (dragIdx !== null && dragIdx !== i) move(dragIdx, i); setDragIdx(null); }}
                className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-grab hover:bg-gray-50 ${dragIdx === i ? 'opacity-50' : ''}`}>
                <input type="checkbox" checked={config.visible.includes(col.key)}
                  onChange={() => toggle(col.key)}
                  className="rounded border-gray-300 text-brand-600 h-3.5 w-3.5" />
                <span className="text-xs text-gray-700 flex-1">{col.label}</span>
                <svg className="w-3 h-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export function SalesRegional() {
  const [tab, setTab] = useState<Tab>('region');
  const [regionalData, setRegionalData] = useState<RegionalResponse | null>(null);
  const [productData, setProductData] = useState<ProductResponse | null>(null);
  const [expandData, setExpandData] = useState<ExpandData | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [expandedParent, setExpandedParent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { displayName, parentNickname } = useNicknames();
  const [colConfig, setColConfig] = useState(loadColumnConfig);
  const [start, setStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [activeDays, setActiveDays] = useState<number | null>(30);

  useEffect(() => {
    setLoading(true);
    setExpandedRow(null);
    setExpandedParent(null);
    const params = `start=${start}&end=${end}`;
    Promise.all([
      api.get<RegionalResponse>(`/sales/regional?${params}`),
      api.get<ProductResponse>(`/sales/by-product?${params}`),
      api.get<ExpandData>(`/sales/by-product-market?${params}`),
    ])
      .then(([r, p, e]) => { setRegionalData(r); setProductData(p); setExpandData(e); })
      .finally(() => setLoading(false));
  }, [start, end]);

  const rt = regionalData?.totals;
  const pt = productData?.totals;

  // Visible columns in configured order
  const visibleCols = colConfig.order
    .filter(k => colConfig.visible.includes(k))
    .map(k => ALL_COLUMNS.find(c => c.key === k)!)
    .filter(Boolean);

  // Get cell value for expand rows (mapped to RegionalRow shape)
  const expandCell = useCallback((col: ColDef, mp: ExpandRow & { sku?: string; asin?: string }) => {
    const asRow: RegionalRow = {
      marketplace: mp.marketplace, country: '', currency: mp.currency, local_currency: mp.currency,
      order_count: mp.orders, units: mp.units, revenue: mp.revenueUsd,
      cogs: mp.cogs ?? 0, profit: mp.profit ?? 0, margin_pct: mp.margin ?? 0,
      fba_fees: mp.fbaFees ?? 0, commission: mp.commission ?? 0, promo: (mp as any).promos ?? 0,
      ppc_spend: mp.ppcSpend ?? 0, ppc_sales: mp.ppcSales ?? 0,
      return_units: (mp as any).refunds ?? 0, return_count: 0,
      net_proceeds: 0, acos: mp.acos ?? 0, tacos: mp.tacos ?? 0,
      refund_rate: mp.units > 0 && (mp as any).refunds ? ((mp as any).refunds / mp.units) * 100 : 0,
      exchange_rate: 1,
    };
    if (col.key === 'country') return displayName(mp.sku ?? '') + (mp.asin ? ` ${mp.asin}` : '');
    return col.getValue(asRow);
  }, [displayName]);

  const expandCellClass = (col: ColDef, mp: ExpandRow) => {
    if (!col.getClass) return '';
    const asRow = { ...mp, margin_pct: mp.margin ?? 0, return_units: 0 } as any;
    return col.getClass(asRow);
  };

  // Group market products by parent ASIN → then expand to child ASINs
  type MarketProduct = ExpandRow & { sku: string; asin: string; parentAsin?: string; parentNickname?: string | null };
  const groupByParentAsin = (products: MarketProduct[]) => {
    const families = new Map<string, { nickname: string; children: MarketProduct[] }>();
    for (const p of products) {
      const key = p.parentAsin || p.asin;
      if (!families.has(key)) {
        families.set(key, { nickname: p.parentNickname || displayName(p.sku), children: [] });
      }
      families.get(key)!.children.push(p);
    }
    return [...families.entries()].map(([parentAsin, { nickname, children }]) => ({
      family: nickname,
      parentAsin,
      children,
      totals: children.reduce((acc, c) => ({
        orders: acc.orders + c.orders, units: acc.units + c.units,
        revenueUsd: acc.revenueUsd + c.revenueUsd, revenueLocal: acc.revenueLocal + c.revenueLocal,
        cogs: acc.cogs + (c.cogs ?? 0), fbaFees: acc.fbaFees + (c.fbaFees ?? 0),
        commission: acc.commission + (c.commission ?? 0), profit: acc.profit + (c.profit ?? 0),
        ppcSpend: acc.ppcSpend + (c.ppcSpend ?? 0), ppcSales: acc.ppcSales + (c.ppcSales ?? 0),
        refunds: acc.refunds + ((c as any).refunds ?? 0),
        promos: acc.promos + ((c as any).promos ?? 0),
      }), { orders: 0, units: 0, revenueUsd: 0, revenueLocal: 0, cogs: 0, fbaFees: 0, commission: 0, profit: 0, ppcSpend: 0, ppcSales: 0, refunds: 0, promos: 0 }),
    })).sort((a, b) => b.totals.revenueUsd - a.totals.revenueUsd);
  };

  return (
    <div className="space-y-6">
      {/* Date range + tabs */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">From</label>
          <input type="date" value={start} onChange={e => { setStart(e.target.value); setActiveDays(null); }}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">To</label>
          <input type="date" value={end} onChange={e => { setEnd(e.target.value); setActiveDays(null); }}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        <div className="flex gap-2">
          {[
            { key: 'today', label: 'Today', days: 0 },
            { key: 'yesterday', label: 'Yesterday', days: -1 },
            { key: '7d', label: '7d', days: 7 },
            { key: '14d', label: '14d', days: 14 },
            { key: '30d', label: '30d', days: 30 },
            { key: '90d', label: '90d', days: 90 },
          ].map(({ key, label, days }) => (
            <button key={key} onClick={() => {
              const today = new Date();
              if (days === 0) {
                const d = today.toISOString().slice(0, 10);
                setStart(d); setEnd(d);
              } else if (days === -1) {
                const y = new Date(today); y.setDate(y.getDate() - 1);
                const d = y.toISOString().slice(0, 10);
                setStart(d); setEnd(d);
              } else {
                const s = new Date(); s.setDate(s.getDate() - days);
                setStart(s.toISOString().slice(0, 10)); setEnd(today.toISOString().slice(0, 10));
              }
              setActiveDays(days);
            }} className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              activeDays === days
                ? 'border-brand-500 bg-brand-50 text-brand-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>
              {label}
            </button>
          ))}
        </div>
        <div className="ml-auto">
          <ColumnConfig config={colConfig} onChange={setColConfig} />
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {([['region', 'By Region'], ['product', 'By Product']] as const).map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`whitespace-nowrap border-b-2 pb-3 pt-1 text-sm font-medium transition-colors ${
                tab === key
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}>{label}</button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center text-gray-400">Loading...</div>
      ) : tab === 'region' ? (
        /* ── By Region ─────────────────────────────────────────────── */
        !regionalData?.rows.length ? (
          <div className="flex h-64 items-center justify-center text-gray-400">No sales data for this period</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Card label="Orders" value={fmtNum(rt!.order_count)} />
              <Card label="Units" value={fmtNum(rt!.units)} />
              <Card label="Revenue" value={fmtUsd(rt!.revenue)} sub="converted to USD" />
              <Card label="Profit" value={fmtUsd(rt!.profit)}
                sub={rt!.margin_pct !== 0 ? `${rt!.margin_pct}% margin` : ''} />
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {visibleCols.map(c => (
                      <th key={c.key} className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {regionalData.rows.map(r => {
                    const isExpanded = expandedRow === `market:${r.marketplace}`;
                    const marketProducts = expandData?.byMarket[r.marketplace] ?? [];
                    const families = groupByParentAsin(marketProducts);

                    return (
                      <Fragment key={r.marketplace}>
                        <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => { setExpandedRow(isExpanded ? null : `market:${r.marketplace}`); setExpandedParent(null); }}>
                          {visibleCols.map(c => (
                            <td key={c.key} className={`px-4 py-3 tabular-nums ${c.align === 'right' ? 'text-right' : ''} ${c.key === 'country' ? 'font-medium whitespace-nowrap' : ''} ${c.getClass?.(r) ?? ''} ${['fba_fees','commission'].includes(c.key) ? 'text-gray-500' : ''}`}>
                              {c.getValue(r)}
                            </td>
                          ))}
                        </tr>
                        {/* Level 1: Product family rows (Walkie Talkies, CarPlay Adapter) */}
                        {isExpanded && families.map(fam => {
                          const famKey = `${r.marketplace}:${fam.family}`;
                          const isFamExpanded = expandedParent === famKey;
                          const t = fam.totals;
                          const famRow: ExpandRow & { sku: string; asin: string } = {
                            marketplace: r.marketplace, currency: r.currency, sku: '', asin: '',
                            orders: t.orders, units: t.units, revenueLocal: t.revenueLocal, revenueUsd: t.revenueUsd,
                            cogs: t.cogs, fbaFees: t.fbaFees, commission: t.commission, profit: t.profit,
                            ppcSpend: t.ppcSpend, ppcSales: t.ppcSales, refunds: t.refunds, promos: t.promos,
                            margin: t.revenueUsd > 0 ? (t.profit / t.revenueUsd) * 100 : 0,
                            acos: t.ppcSpend > 0 && t.ppcSales > 0 ? (t.ppcSpend / t.ppcSales) * 100 : 0,
                            tacos: t.revenueUsd > 0 ? (t.ppcSpend / t.revenueUsd) * 100 : 0,
                          };

                          return (
                            <Fragment key={famKey}>
                              <tr className="bg-blue-50/40 hover:bg-blue-50 cursor-pointer"
                                onClick={e => { e.stopPropagation(); setExpandedParent(isFamExpanded ? null : famKey); }}>
                                {visibleCols.map(c => (
                                  <td key={c.key} className={`px-4 py-2 text-xs tabular-nums ${c.align === 'right' ? 'text-right' : ''} ${expandCellClass(c, famRow)}`}>
                                    {c.key === 'country' ? (
                                      <span className="pl-6 flex items-center gap-1.5">
                                        <svg className={`w-3 h-3 text-gray-400 transition-transform ${isFamExpanded ? 'rotate-90' : ''}`}
                                          fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                        <span className="font-semibold text-gray-800">{fam.family}</span>
                                        <span className="text-[10px] text-gray-400 font-mono">{fam.parentAsin}</span>
                                        <span className="text-[10px] text-gray-400">({fam.children.length} {fam.children.length === 1 ? 'variant' : 'variants'})</span>
                                      </span>
                                    ) : expandCell(c, famRow)}
                                  </td>
                                ))}
                              </tr>
                              {/* Level 2: Individual variation rows */}
                              {isFamExpanded && fam.children.map((child, ci) => (
                                <tr key={`${famKey}-${child.sku}-${ci}`} className="bg-gray-50/50">
                                  {visibleCols.map(c => (
                                    <td key={c.key} className={`px-4 py-1.5 text-[11px] tabular-nums ${c.align === 'right' ? 'text-right' : ''} ${expandCellClass(c, child)} text-gray-500`}>
                                      {c.key === 'country' ? (
                                        <span className="pl-14 text-gray-500 flex items-center gap-1.5">
                                          <span>{displayName(child.sku)}</span>
                                          <span className="text-gray-300 font-mono">{child.asin}</span>
                                        </span>
                                      ) : expandCell(c, child)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </Fragment>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </tbody>
                {regionalData.rows.length > 1 && (
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                      {visibleCols.map(c => {
                        if (c.key === 'country') return <td key={c.key} className="px-4 py-3">Total ({regionalData.rows.length} markets)</td>;
                        const totRow: RegionalRow = {
                          ...rt!, marketplace: '', country: '', currency: 'USD', local_currency: 'USD', exchange_rate: 1,
                        };
                        return (
                          <td key={c.key} className={`px-4 py-3 tabular-nums ${c.align === 'right' ? 'text-right' : ''} ${c.getClass?.(totRow) ?? ''}`}>
                            {c.getValue(totRow)}
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )
      ) : (
        /* ── By Product ────────────────────────────────────────────── */
        !productData?.products.length ? (
          <div className="flex h-64 items-center justify-center text-gray-400">No product sales for this period</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Card label="Orders" value={fmtNum(pt!.orders)} />
              <Card label="Units" value={fmtNum(pt!.units)} />
              <Card label="Revenue" value={fmtUsd(pt!.revenue)} sub="converted to USD" />
              <Card label="Profit" value={fmtUsd(pt!.profit)}
                sub={pt!.margin !== 0 ? `${pt!.margin}% margin` : ''} />
            </div>

            {(() => {
              // Build enriched product rows from expand data
              const enrichedProducts = productData!.products.map(p => {
                const markets = expandData?.byProduct[p.sku] ?? [];
                const sum = (fn: (m: ExpandRow) => number) => markets.reduce((s, m) => s + fn(m), 0);
                const rev = sum(m => m.revenueUsd);
                const pSpend = sum(m => m.ppcSpend ?? 0);
                const pSales = sum(m => m.ppcSales ?? 0);
                const refunds = sum(m => (m as any).refunds ?? 0);
                const row: RegionalRow = {
                  marketplace: p.sku, country: p.title, currency: 'USD', local_currency: 'USD',
                  order_count: p.orders, units: p.units, revenue: rev > 0 ? rev : p.revenue,
                  cogs: sum(m => m.cogs ?? 0) || p.cogs, profit: sum(m => m.profit ?? 0) || p.profit,
                  margin_pct: rev > 0 ? ((sum(m => m.profit ?? 0)) / rev) * 100 : p.margin,
                  fba_fees: sum(m => m.fbaFees ?? 0), commission: sum(m => m.commission ?? 0),
                  promo: sum(m => (m as any).promos ?? 0),
                  ppc_spend: pSpend, ppc_sales: pSales,
                  return_units: refunds, return_count: 0, net_proceeds: 0,
                  acos: pSales > 0 ? (pSpend / pSales) * 100 : 0,
                  tacos: rev > 0 ? (pSpend / rev) * 100 : 0,
                  refund_rate: p.units > 0 ? (refunds / p.units) * 100 : 0,
                  exchange_rate: 1,
                };
                return { ...p, row };
              });
              // Product-level totals
              const prodTotals: RegionalRow = enrichedProducts.reduce((t, ep) => ({
                ...t,
                order_count: t.order_count + ep.row.order_count, units: t.units + ep.row.units,
                revenue: t.revenue + ep.row.revenue, cogs: t.cogs + ep.row.cogs, profit: t.profit + ep.row.profit,
                fba_fees: t.fba_fees + ep.row.fba_fees, commission: t.commission + ep.row.commission,
                promo: t.promo + ep.row.promo, ppc_spend: t.ppc_spend + ep.row.ppc_spend,
                ppc_sales: t.ppc_sales + ep.row.ppc_sales, return_units: t.return_units + ep.row.return_units,
              }), { marketplace: '', country: 'Total', currency: 'USD', local_currency: 'USD',
                order_count: 0, units: 0, revenue: 0, cogs: 0, profit: 0, margin_pct: 0,
                fba_fees: 0, commission: 0, promo: 0, ppc_spend: 0, ppc_sales: 0,
                return_units: 0, return_count: 0, net_proceeds: 0, acos: 0, tacos: 0, refund_rate: 0, exchange_rate: 1,
              } as RegionalRow);
              prodTotals.margin_pct = prodTotals.revenue > 0 ? (prodTotals.profit / prodTotals.revenue) * 100 : 0;
              prodTotals.acos = prodTotals.ppc_sales > 0 ? (prodTotals.ppc_spend / prodTotals.ppc_sales) * 100 : 0;
              prodTotals.tacos = prodTotals.revenue > 0 ? (prodTotals.ppc_spend / prodTotals.revenue) * 100 : 0;

              // Product columns: replace 'country' with 'product'
              const prodCols = visibleCols.map(c => c.key === 'country' ? { ...c, key: 'product', label: 'Product' } : c);

              return (
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        {prodCols.map(c => (
                          <th key={c.key} className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {enrichedProducts.map(ep => {
                        const p = ep;
                        const isExpanded = expandedRow === `product:${p.sku}`;
                        const skuMarkets = expandData?.byProduct[p.sku] ?? [];
                        return (
                          <Fragment key={p.sku}>
                            <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedRow(isExpanded ? null : `product:${p.sku}`)}>
                              {prodCols.map(c => (
                                <td key={c.key} className={`px-4 py-3 tabular-nums ${c.align === 'right' ? 'text-right' : ''} ${c.getClass?.(ep.row) ?? ''} ${['fba_fees','commission'].includes(c.key) ? 'text-gray-500' : ''}`}>
                                  {c.key === 'product' ? (
                                    <div className="flex items-center gap-3">
                                      {p.imageUrl ? (
                                        <img src={resolveImageUrl(p.imageUrl) ?? ''} alt="" className="h-9 w-9 rounded border border-gray-100 object-contain flex-shrink-0" loading="lazy" />
                                      ) : (
                                        <div className="h-9 w-9 rounded border border-gray-100 bg-gray-50 flex-shrink-0" />
                                      )}
                                      <div className="min-w-0">
                                        {parentNickname(p.sku) && <p className="text-[10px] text-gray-400">{parentNickname(p.sku)}</p>}
                                        <p className="text-sm font-medium text-gray-900 truncate max-w-[250px]">{displayName(p.sku)}</p>
                                      </div>
                                    </div>
                                  ) : c.getValue(ep.row)}
                                </td>
                              ))}
                            </tr>
                            {isExpanded && skuMarkets.length > 0 && skuMarkets.map((sm, i) => (
                              <tr key={`${p.sku}-${sm.marketplace}-${i}`} className="bg-gray-50/50">
                                {prodCols.map(c => (
                                  <td key={c.key} className={`px-4 py-2 text-xs tabular-nums ${c.align === 'right' ? 'text-right' : ''} ${expandCellClass(c, sm)} text-gray-500`}>
                                    {c.key === 'product' ? (
                                      <span className="pl-12">
                                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">{MP_NAMES[sm.marketplace] ?? sm.marketplace}</span>
                                      </span>
                                    ) : expandCell(c, sm)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </Fragment>
                        );
                      })}
                    </tbody>
                    {enrichedProducts.length > 1 && (
                      <tfoot>
                        <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                          {prodCols.map(c => (
                            <td key={c.key} className={`px-4 py-3 tabular-nums ${c.align === 'right' ? 'text-right' : ''} ${c.getClass?.(prodTotals) ?? ''}`}>
                              {c.key === 'product' ? `Total (${enrichedProducts.length} products)` : c.getValue(prodTotals)}
                            </td>
                          ))}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              );
            })()}
          </>
        )
      )}
    </div>
  );
}

function Card({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
    </div>
  );
}
